import { LinkExpirationMeaning } from 'phoenix/domain';
import { DateService } from 'phoenix/services';
import * as moment from 'moment';

require('./expiration-picker.styles');
const template = require('./expiration-picker.template');

const DEFAULT_EXPIRATION_DAYS = 30;

export interface ExpirationDateChangedParameter {
	updatedDate:Date,
	expirationMeaning:LinkExpirationMeaning,
	expirationValue:number | Date
}

export class ExpirationPickerController implements ng.IComponentController {
	//From isolated scope of component
	public defaultExpirationMeaning:LinkExpirationMeaning = angular.isDefined(this.defaultExpirationMeaning) ? this.defaultExpirationMeaning : LinkExpirationMeaning.Date;
	public defaultExpirationValue:number | Date = angular.isDefined(this.defaultExpirationValue) ? this.defaultExpirationValue : moment().add(1, 'days').toDate();
	public readonly maxExpirationDays:number;
	public readonly maxExpirationDate:Date;
	public readonly minExpirationDate:Date = new Date();
	private readonly onExpirationChanged:(expirationParams:ExpirationDateChangedParameter) => void;
	
	public datePickerDate:Date;
	public expirationMeaning:LinkExpirationMeaning;
	public expirationValue:number | Date;
	public innerForm:ng.IFormController;

	public static $inject = [
		'dateFilter',
		'pxDateService',
		'gettextCatalog'
	];

	constructor(
		private dateFilter:ng.IFilterDate,
		private readonly pxDateService:DateService,
		private readonly gettextCatalog:ng.gettext.gettextCatalog
	) {
		this.validateDefaults();

		this.expirationMeaning = this.defaultExpirationMeaning;
		this.expirationValue = this.hasNumericExpiration ? this.defaultExpirationValue as number : DEFAULT_EXPIRATION_DAYS;
		this.datePickerDate = this.defaultExpirationMeaning === LinkExpirationMeaning.Date ? new Date(this.defaultExpirationValue) : this.minExpirationDate;

		if(this.maxExpirationDays){
			let dateFromMaxDays = moment().add(this.maxExpirationDays, 'days');
			
			// if the both days & date were provided, use the lesser of the two
			if(!this.maxExpirationDate || dateFromMaxDays.isBefore(this.maxExpirationDate, 'day')){
				this.maxExpirationDate = dateFromMaxDays.toDate();
			}
		}
	}

	public get hasNumericExpiration() {
		return this.expirationMeaning === LinkExpirationMeaning.Minutes
			|| this.expirationMeaning === LinkExpirationMeaning.Hours
			|| this.expirationMeaning === LinkExpirationMeaning.Days;
	}

	public get numericExpirationPlaceholder() {
		let translatedUnits = this.linkExpirationMeaningStrings.get(this.expirationMeaning);
		return this.gettextCatalog.getString('Enter {{units}}', {units: translatedUnits});
	}

	public get maxDateValidationPhrase(){
		if(this.expirationMeaning === LinkExpirationMeaning.Date) {
			return this.gettextCatalog.getString('The expiration date must be before {{expDate}}', { expDate: this.dateFilter(this.maxExpirationDate, 'longDate') });
		}
		else if(this.hasNumericExpiration)  {			
			let translatedUnits = this.linkExpirationMeaningStrings.get(this.expirationMeaning);
			return this.gettextCatalog.getString('The expiration date must be less than {{qty}} {{units}} out', { qty: this.maxExpirationDays, units: translatedUnits })
		}
	}

	public get maxExpirationValue() {
		const maxExpirationVal = 9999999;
		
		if(!this.maxExpirationDays) {
			return maxExpirationVal;
		}

		switch (this.expirationMeaning) {
			case LinkExpirationMeaning.Days:
				return this.maxExpirationDays;

			case LinkExpirationMeaning.Hours:
				return this.maxExpirationDays * 24;

			case LinkExpirationMeaning.Minutes:
				return this.maxExpirationDays * 24 * 60;
			
			default:
				return maxExpirationVal;
		}
	}

	public onExpirationMeaningChanged() {
		if(this.expirationMeaning === LinkExpirationMeaning.Date) {
			this.onDatePicked();
		}
		else {
			this.onExpirationValueChanged();
		}
	}

	public onExpirationValueChanged() {
		if(this.onExpirationChanged) {
			this.onExpirationChanged({
				updatedDate: this.computeExpirationDate(),
				expirationMeaning: this.expirationMeaning,
				expirationValue: this.expirationValue
			});
		}
	}

	public onDatePicked() {
		if(this.onExpirationChanged) {
			this.onExpirationChanged({
				updatedDate: this.computeExpirationDate(),
				expirationMeaning: this.expirationMeaning,
				expirationValue: this.datePickerDate
			});
		}
	}

	private computeExpirationDate() {
		let expirationDate:Date = this.pxDateService.maxDate;

		switch(this.expirationMeaning) {
			case LinkExpirationMeaning.Days:
			case LinkExpirationMeaning.Hours:
			case LinkExpirationMeaning.Minutes:
				expirationDate = this.pxDateService.getRelativeDate(this.expirationMeaning, this.expirationValue as number);
				break;
			case LinkExpirationMeaning.Date:
				expirationDate = this.datePickerDate;
				break;
			case LinkExpirationMeaning.Never:
				expirationDate = this.pxDateService.maxDate;
				break;
			default:
				throw new Error('Unsupported expiration meaning');
		}

		return expirationDate;
	}

	private validateDefaults() {
		const hasNumericDefaultMeaning = this.defaultExpirationMeaning === LinkExpirationMeaning.Days
										|| this.defaultExpirationMeaning === LinkExpirationMeaning.Hours
										|| this.defaultExpirationMeaning === LinkExpirationMeaning.Minutes;

		if(this.defaultExpirationMeaning === LinkExpirationMeaning.Date && !(this.defaultExpirationValue instanceof Date)) {
			throw new Error('Default expiration-picker expiration value is not a date, but was expected to be');
		}
		else if(hasNumericDefaultMeaning && isNaN(this.defaultExpirationValue as number)) {
			throw new Error('Default expiration-picker expiration value is not a number, but was expected to be');
		}
		else if(hasNumericDefaultMeaning && this.defaultExpirationValue < 1) {
			throw new Error('Default expiration-picker expiration value must be a positive number');
		}
	}

	private readonly linkExpirationMeaningStrings = new Map<LinkExpirationMeaning, string>([
		[LinkExpirationMeaning.Date, this.gettextCatalog.getString('Date')],
		[LinkExpirationMeaning.Days, this.gettextCatalog.getString('Days')],
		[LinkExpirationMeaning.Hours, this.gettextCatalog.getString('Hours')],
		[LinkExpirationMeaning.Minutes, this.gettextCatalog.getString('Minutes')],
		[LinkExpirationMeaning.Never, this.gettextCatalog.getString('Never')],
		[LinkExpirationMeaning.SingleUse, this.gettextCatalog.getString('Single Use')],
	]);
}

export const ExpirationPickerComponent:ng.IComponentOptions = {
	template: template,
	controller: ExpirationPickerController,
	controllerAs: 'vm',
	bindings: {
		defaultExpirationMeaning: '<?',
		defaultExpirationValue: '<?',
		maxExpirationDays: '<?',
		maxExpirationDate: '<?',
		minExpirationDate: '<?',
		onExpirationChanged: '&?'
	}
}


// WEBPACK FOOTER //
// ./src/sharebase/components/expiration-picker/expiration-picker.ts